import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useSetupGuide from "../../../hooks/useSetupGuide";
import {
  ChipCopy,
  ChipProgress,
  ChipRemaining,
  ChipTitle,
  SetupGuideChipButton,
} from "./style";

function ProgressRing({ remaining, total = 5 }) {
  const size = 20;
  const stroke = 2;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const completed = Math.max(0, Math.min(total, total - (remaining ?? 0)));
  const progress = total > 0 ? completed / total : 0;
  const offset = circumference * (1 - progress);

  return (
    <ChipProgress aria-hidden>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#E4E6EA"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#073B4C"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
    </ChipProgress>
  );
}

export default function SetupGuideChip({ token, className }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { loading, remainingSteps, error } = useSetupGuide(token);

  // Always show the nav entry for sellers so Setup Guide stays discoverable
  // even when the API is unavailable or all steps are complete.
  if (!token) return null;
  if (loading && remainingSteps == null && !error) {
    return (
      <SetupGuideChipButton
        type="button"
        className={className}
        onClick={() => navigate("/setup-guide")}
        aria-label={t("setupGuide.title", "Setup Guide")}
        disabled
        style={{ opacity: 0.7 }}
      >
        <ProgressRing remaining={5} />
        <ChipCopy>
          <ChipTitle>{t("setupGuide.title", "Setup Guide")}</ChipTitle>
          <ChipRemaining>
            {t("setupGuide.loading", "Loading…")}
          </ChipRemaining>
        </ChipCopy>
      </SetupGuideChipButton>
    );
  }

  const remaining = remainingSteps == null ? null : Number(remainingSteps);
  const subtitle =
    remaining == null
      ? t("setupGuide.openGuide", "Open setup guide")
      : remaining <= 0
        ? t("setupGuide.allComplete", "All steps complete")
        : t("setupGuide.stepsRemaining", "{{count}} steps remaining", {
            count: remaining,
          });

  return (
    <SetupGuideChipButton
      type="button"
      className={className}
      onClick={() => navigate("/setup-guide")}
      aria-label={t("setupGuide.title", "Setup Guide")}
    >
      <ProgressRing remaining={remaining == null ? 5 : remaining} />
      <ChipCopy>
        <ChipTitle>{t("setupGuide.title", "Setup Guide")}</ChipTitle>
        <ChipRemaining $muted={remaining != null && remaining <= 0}>
          {subtitle}
        </ChipRemaining>
      </ChipCopy>
    </SetupGuideChipButton>
  );
}
